import type { Tank } from "./api";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
export const STALE_AFTER_MS = 12 * HOUR;

const LEVELS: Record<string, string> = { full: "Full", not_full: "Not full" };

export function age(observedAt: string, now: Date): string {
  const ms = Math.max(0, now.getTime() - new Date(observedAt).getTime());
  if (ms < MINUTE) return "just now";
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)} min ago`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)} h ago`;
  return `${Math.floor(ms / DAY)} d ago`;
}

export function isStale(observedAt: string, now: Date): boolean {
  return now.getTime() - new Date(observedAt).getTime() > STALE_AFTER_MS;
}

export function readingTime(observedAt: string, timeZone?: string): string {
  return new Date(observedAt).toLocaleString(undefined, {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export function clockTime(at: Date, timeZone?: string): string {
  return at.toLocaleTimeString(undefined, { timeZone, hour: "numeric", minute: "2-digit", timeZoneName: "short" });
}

export function attention(tank: Tank, now: Date): "stale" | "full" | "ok" {
  if (!tank.state || isStale(tank.state.observed_at, now)) return "stale";
  return tank.state.measurement.level_state === "full" ? "full" : "ok";
}

const RANK = { stale: 0, full: 1, ok: 2 };

/** Rule T-4: a test tank comes after all tanks in the field and needs no attention. */
export function byAttention(now: Date) {
  return (a: Tank, b: Tank) =>
    Number(!a.in_field) - Number(!b.in_field) || RANK[attention(a, now)] - RANK[attention(b, now)] || byLabel(a, b);
}

/** Rule T-4: the counts are of the tanks in the field. */
export function summary(tanks: Tank[], now: Date): string {
  const field = tanks.filter((t) => t.in_field);
  const full = field.filter((t) => t.state?.measurement.level_state === "full").length;
  const stale = field.filter((t) => attention(t, now) === "stale").length;
  const parts = [`${field.length} ${field.length === 1 ? "container" : "containers"}`];
  if (full) parts.push(`${full} full`);
  if (stale) parts.push(`${stale} stale or not reporting`);
  if (field.length < tanks.length) parts.push(`${tanks.length - field.length} test`);
  return parts.join(" · ");
}

export function level(tank: Tank): string {
  const state = tank.state?.measurement.level_state;
  if (state === undefined) return "No reading";
  return LEVELS[state] ?? state;
}

export function battery(tank: Tank): string {
  const pct = tank.state?.quality.battery_pct;
  return typeof pct === "number" ? `${Math.round(pct)}%` : "Not reported";
}

export const LOW_BATTERY_PCT = 20;

export function lowBattery(tank: Tank): boolean {
  const pct = tank.state?.quality.battery_pct;
  return typeof pct === "number" && pct < LOW_BATTERY_PCT;
}

export function byLabel(a: Tank, b: Tank): number {
  return a.label.localeCompare(b.label, undefined, { numeric: true });
}

/** A calendar date (YYYY-MM-DD) in the user's locale: "Oct 26", or "Mon" for the weekday, or "Mon Oct 26" for both. */
export function dateLabel(date: string, part: "date" | "weekday" | "both" = "date"): string {
  const at = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  const weekday = at.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
  const day = at.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  return part === "weekday" ? weekday : part === "date" ? day : `${weekday} ${day}`;
}
